import { asList } from '../lib/api';
import { useState, useEffect, useCallback } from 'react';
import { useResolvedCampus } from '../lib/useCampus';
import {
  masterData,
  type Named, type SubjectRow, type ClassRow, type RoomRow,
  type LeaveTypeRow, type VehicleRow, type CampusRow, type InstitutionRow,
} from '../lib/masterDataApi';

/**
 * ═══ MASTER DATA — the lists a school types once and lives with for a decade ═══
 *
 * ⚠ WHY THIS SCREEN EXISTS. A sweep of every domain entity against the verbs actually exposed found
 * fourteen types a school could CREATE and then never correct: subjects, classes, rooms,
 * departments, designations, leave types, vehicles, hostel blocks, store items, sports equipment,
 * fixtures, exams, campuses and the institution itself. Not one of them had an edit screen. A
 * subject typed "Mathamatics" printed on every marksheet, forever.
 *
 * The domain had the methods the whole time — `Subject.Update`, `Vehicle.Update`, `Room.Deactivate`,
 * `HostelBlock.Update` — written and commented and unreachable. The work was done; nothing was
 * wired to it.
 *
 * ⚠ ONE SCREEN RATHER THAN AN EDIT BUTTON ON EIGHT PAGES, deliberately. This is how an office
 * actually thinks about it: "where do I fix the lists". Scattering the same capability across the
 * timetable page, the HR page and the transport page means a clerk who needs to rename a department
 * has to know which module owns it.
 *
 * ⚠ NOTHING HERE DELETES. Every one of these is a join key — employees point at departments, marks
 * at subjects, timetable slots at rooms. The verb is RETIRE, and it is reversible, because a
 * mis-click that cannot be undone is the same trap one level down.
 */

type Tab = 'subjects' | 'classes' | 'rooms' | 'hr' | 'transport' | 'trust';

/**
 * ⚠ WHICH TABS BELONG TO ONE INSTITUTION, and which are trust-wide. Subjects, classes and rooms are
 * defined per institution — the school's Class 8 and the college's Semester 3 are different lists.
 * Departments, designations, leave types and vehicles are shared across the trust: one HR structure,
 * one bus fleet. Showing "which institution?" above a bus list would be asking a question with no
 * answer.
 */
const NEEDS_INSTITUTION = new Set<Tab>(['subjects', 'classes', 'rooms']);

/**
 * ⚠ THE ENUM NAME IS NOT A LABEL. `PuCollege` and `EngineeringCollege` are C# identifiers; printed
 * raw they read as a developer's variable leaking onto a principal's screen. Unknown values fall
 * through unchanged rather than rendering blank, so a type added later is ugly, not invisible.
 */
const TYPE_LABEL: Record<string, string> = {
  School: 'School',
  PuCollege: 'PU College',
  EngineeringCollege: 'Engineering College',
  DegreeCollege: 'Degree College',
  CoachingInstitute: 'Coaching Institute',
  TrainingAcademy: 'Training Academy',
  Other: 'Other',
};
const BOARD_LABEL: Record<string, string> = {
  Cbse: 'CBSE', Icse: 'ICSE', StateBoard: 'State Board',
  PreUniversity: 'Pre-University', University: 'University',
};

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'subjects', label: 'Subjects' },
  { key: 'classes', label: 'Classes' },
  { key: 'rooms', label: 'Rooms' },
  { key: 'hr', label: 'Staff lists' },
  { key: 'transport', label: 'Vehicles' },
  { key: 'trust', label: 'Campuses & institutions' },
];

export function MasterDataPage() {
  const [tab, setTab] = useState<Tab>('subjects');
  /**
   * ⚠ useResolvedCampus, NOT useCampus. Subjects, classes and rooms belong to ONE institution, and
   * a plain `useCampus()` answers null for a single-campus school whose token never carried the
   * claim — which is most schools. This screen would then greet a principal with "no campus
   * selected" and nothing to select it with. Resolved: one institution means one answer; two or
   * more means ask, and `choices` is what the asking is drawn from.
   */
  const { institutionId, choices } = useResolvedCampus();
  const [picked, setPicked] = useState<string | null>(null);
  const effective = institutionId ?? picked;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Settings</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Master data</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: 620 }}>
          The lists everything else refers to. Correct a name here and it is corrected everywhere it
          appears from now on — nothing already printed changes, which is right: a certificate issued
          in 2024 should still read as it did in 2024.
        </p>
      </div>

      <div className="tab-strip" style={{ gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button key={t.key} className={tab === t.key ? 'btn btn-accent' : 'btn'} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <Note>
        Nothing on this screen can be deleted. Each of these lists is what the rest of the product
        points at, so removing a row would strand the records that use it. <strong>Retire</strong>{' '}
        takes it out of every picker and leaves the past readable — and you can bring it back.
      </Note>

      {/* ⚠ Only shown when the answer is genuinely ambiguous. A trust running a school and a
          college must say which one it is renaming a subject in; picking the first would be the
          "whichever campus the first student row belongs to" bug wearing a new name. */}
      {NEEDS_INSTITUTION.has(tab) && !institutionId && choices.length > 1 && (
        <div className="card" style={{ padding: 12, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Subjects, classes and rooms belong to one institution. Which one?</span>
          <select className="input" value={picked ?? ''} onChange={(e) => setPicked(e.target.value || null)}>
            <option value="">Choose…</option>
            {choices.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {tab === 'subjects' && <Subjects institutionId={effective} />}
      {tab === 'classes' && <Classes institutionId={effective} />}
      {tab === 'rooms' && <Rooms institutionId={effective} />}
      {tab === 'hr' && <StaffLists />}
      {tab === 'transport' && <Vehicles />}
      {tab === 'trust' && <Trust />}
    </div>
  );
}

/* ─────────────────────────── shared bits ─────────────────────────── */

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="card" style={{ padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: 'var(--ink-2)', background: 'var(--surface-2,#f6f6f4)' }}>
      {children}
    </div>
  );
}

/**
 * ⚠ A RETIRED ROW STAYS ON SCREEN, dimmed, with a Restore button. The alternative — hiding it — is
 * what makes an accidental retire unrecoverable: the clerk cannot see the thing they need to undo,
 * so they create a replacement, which mints a new id and leaves every existing record attached to
 * the row they meant to fix.
 */
function Row({ active, children }: { active: boolean; children: React.ReactNode }) {
  return (
    <div
      className="card"
      style={{
        padding: 12, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        opacity: active ? 1 : 0.55,
      }}
    >
      {children}
    </div>
  );
}

function RetiredPill() {
  return <span className="pill" style={{ fontSize: 11, background: 'var(--surface-3,#eee)' }}>Retired</span>;
}

/** The error a failed save produced. Shown rather than swallowed — a domain rule refusing an edit
 *  ("marks are already entered, so the maximum stays at 50") is the most useful text on the page. */
function useSaver(reload: () => void) {
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async (fn: () => Promise<unknown>) => {
    setError(null);
    try { await fn(); reload(); }
    catch (e) { setError(e instanceof Error ? e.message : 'That could not be saved.'); }
  }, [reload]);
  return { error, run };
}

function ErrorLine({ error }: { error: string | null }) {
  if (!error) return null;
  return <div style={{ color: 'var(--brand,#c1272d)', fontSize: 12.5, padding: '6px 2px 10px' }}>{error}</div>;
}

/** ⚠ Shown only when a trust has MORE THAN ONE institution and has not said which. On a
 *  single-campus school this never renders — see useResolvedCampus. */
function ChooseCampusFirst() {
  return (
    <div style={{ color: 'var(--ink-3)', padding: 12, fontSize: 13 }}>
      Choose an institution above to see its subjects, classes and rooms.
    </div>
  );
}

function Empty({ what }: { what: string }) {
  return <div style={{ color: 'var(--ink-3)', padding: 12, fontSize: 13 }}>No {what} yet.</div>;
}

/* ─────────────────────────── subjects ─────────────────────────── */

function Subjects({ institutionId }: { institutionId: string | null }) {
  const [rows, setRows] = useState<SubjectRow[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const load = useCallback(() => {
    if (!institutionId) return;
    masterData.subjects(institutionId).then((r) => setRows(asList(r))).catch(() => setRows([]));
  }, [institutionId]);
  useEffect(load, [load]);
  const { error, run } = useSaver(() => { setEditing(null); load(); });

  if (!institutionId) return <ChooseCampusFirst />;
  if (rows.length === 0) return <Empty what="subjects" />;

  return (
    <div>
      <ErrorLine error={error} />
      <Note>
        The <strong>code</strong> and the <strong>semester</strong> cannot be changed here. Both are
        identifiers rather than labels — marks, credits and the timetable are keyed on them, so
        editing one would re-file work already recorded. A genuinely wrong code is a new subject.
      </Note>
      {rows.map((s) => (
        <Row key={s.id} active={s.isActive}>
          <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-3)', width: 84 }}>{s.code}</span>
          {editing === s.id ? (
            <SubjectEdit row={s} onSave={(b) => run(() => masterData.updateSubject(s.id, b))} onCancel={() => setEditing(null)} />
          ) : (
            <>
              <span style={{ flex: 1, fontWeight: 600 }}>{s.name}</span>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Sem {s.semester} · {s.credits} cr</span>
              {!s.isActive && <RetiredPill />}
              <button className="btn" style={{ padding: '2px 10px' }} onClick={() => setEditing(s.id)}>Edit</button>
              <button
                className="btn"
                style={{ padding: '2px 10px' }}
                onClick={() => run(() => masterData.updateSubject(s.id, { name: s.name, credits: s.credits, isActive: !s.isActive }))}
              >
                {s.isActive ? 'Retire' : 'Restore'}
              </button>
            </>
          )}
        </Row>
      ))}
    </div>
  );
}

function SubjectEdit({ row, onSave, onCancel }: {
  row: SubjectRow;
  onSave: (b: { name: string; credits: number; isActive: boolean }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [credits, setCredits] = useState(String(row.credits));
  return (
    <>
      <input className="input" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
      <input className="input" type="number" value={credits} onChange={(e) => setCredits(e.target.value)} style={{ width: 74 }} title="Credits" />
      <button className="btn btn-accent" style={{ padding: '2px 12px' }}
        onClick={() => onSave({ name: name.trim(), credits: Number(credits) || 0, isActive: row.isActive })}>Save</button>
      <button className="btn" style={{ padding: '2px 10px' }} onClick={onCancel}>Cancel</button>
    </>
  );
}

/* ─────────────────────────── classes ─────────────────────────── */

function Classes({ institutionId }: { institutionId: string | null }) {
  const [rows, setRows] = useState<ClassRow[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const load = useCallback(() => {
    if (!institutionId) return;
    masterData.classes(institutionId).then((r) => setRows(asList(r))).catch(() => setRows([]));
  }, [institutionId]);
  useEffect(load, [load]);
  const { error, run } = useSaver(() => { setEditing(null); load(); });

  if (!institutionId) return <ChooseCampusFirst />;
  if (rows.length === 0) return <Empty what="classes" />;

  return (
    <div>
      <ErrorLine error={error} />
      <Note>
        Only the <strong>section</strong> can be corrected (and, for a batch, its name). Branch and
        semester are what a class MEANS — changing them would silently re-file every student, mark,
        invoice and attendance row attached to it while the class id stayed the same.
      </Note>
      {rows.map((c) => (
        <Row key={c.id} active={c.isActive}>
          {editing === c.id ? (
            <ClassEdit row={c} onSave={(b) => run(() => masterData.renameClass(c.id, b))} onCancel={() => setEditing(null)} />
          ) : (
            <>
              <span style={{ flex: 1, fontWeight: 600 }}>{c.label}</span>
              {c.isBatch && <span className="pill" style={{ fontSize: 11 }}>Batch</span>}
              {c.classTeacherName && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{c.classTeacherName}</span>}
              {!c.isActive && <RetiredPill />}
              <button className="btn" style={{ padding: '2px 10px' }} onClick={() => setEditing(c.id)}>Edit</button>
              <button className="btn" style={{ padding: '2px 10px' }}
                onClick={() => run(() => masterData.setClassActive(c.id, !c.isActive))}>
                {c.isActive ? 'Close' : 'Reopen'}
              </button>
            </>
          )}
        </Row>
      ))}
    </div>
  );
}

function ClassEdit({ row, onSave, onCancel }: {
  row: ClassRow;
  onSave: (b: { section?: string | null; batchName?: string | null }) => void;
  onCancel: () => void;
}) {
  const [section, setSection] = useState(row.section);
  const [batchName, setBatchName] = useState(row.branch);
  return (
    <>
      {row.isBatch && (
        <input className="input" value={batchName} onChange={(e) => setBatchName(e.target.value)}
          placeholder="Batch name" style={{ flex: 1, minWidth: 180 }} />
      )}
      <input className="input" value={section} onChange={(e) => setSection(e.target.value)}
        placeholder="Section" style={{ width: 90 }} />
      <button className="btn btn-accent" style={{ padding: '2px 12px' }}
        onClick={() => onSave(row.isBatch ? { section, batchName } : { section })}>Save</button>
      <button className="btn" style={{ padding: '2px 10px' }} onClick={onCancel}>Cancel</button>
    </>
  );
}

/* ─────────────────────────── rooms ─────────────────────────── */

function Rooms({ institutionId }: { institutionId: string | null }) {
  const [rows, setRows] = useState<RoomRow[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const load = useCallback(() => { masterData.rooms(true).then((r) => setRows(asList(r))).catch(() => setRows([])); }, []);
  useEffect(load, [load]);
  const { error, run } = useSaver(() => { setEditing(null); load(); });

  if (rows.length === 0) return <Empty what="rooms" />;

  return (
    <div>
      <ErrorLine error={error} />
      <Note>
        Renaming happens in place. Every timetable slot books a room <em>by id</em>, so replacing the
        row instead would silently unbook the whole weekly grid — and the clash detector would stop
        seeing the conflicts it exists to catch.
      </Note>
      {rows.map((r) => (
        <Row key={r.id} active={r.isActive}>
          {editing === r.id ? (
            <RoomEdit row={r}
              onSave={(b) => run(() => masterData.updateRoom(r.id, { institutionId: institutionId ?? '', ...b }))}
              onCancel={() => setEditing(null)} />
          ) : (
            <>
              <span style={{ flex: 1, fontWeight: 600 }}>{r.name}</span>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{r.capacity ? `seats ${r.capacity}` : 'capacity not set'}</span>
              {!r.isActive && <RetiredPill />}
              <button className="btn" style={{ padding: '2px 10px' }} onClick={() => setEditing(r.id)}>Edit</button>
              <button className="btn" style={{ padding: '2px 10px' }}
                onClick={() => run(() => masterData.setRoomActive(r.id, !r.isActive))}>
                {r.isActive ? 'Out of service' : 'Back in service'}
              </button>
            </>
          )}
        </Row>
      ))}
    </div>
  );
}

function RoomEdit({ row, onSave, onCancel }: {
  row: RoomRow;
  onSave: (b: { name: string; capacity: number | null }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [cap, setCap] = useState(row.capacity == null ? '' : String(row.capacity));
  return (
    <>
      <input className="input" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
      <input className="input" type="number" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="Seats" style={{ width: 90 }} />
      <button className="btn btn-accent" style={{ padding: '2px 12px' }}
        onClick={() => onSave({ name: name.trim(), capacity: cap === '' ? null : Number(cap) })}>Save</button>
      <button className="btn" style={{ padding: '2px 10px' }} onClick={onCancel}>Cancel</button>
    </>
  );
}

/* ─────────────────────────── staff lists ─────────────────────────── */

function StaffLists() {
  const [depts, setDepts] = useState<Named[]>([]);
  const [desigs, setDesigs] = useState<Named[]>([]);
  const [types, setTypes] = useState<LeaveTypeRow[]>([]);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(() => {
    masterData.departments(true).then((r) => setDepts(asList(r))).catch(() => setDepts([]));
    masterData.designations(true).then((r) => setDesigs(asList(r))).catch(() => setDesigs([]));
    masterData.leaveTypes(true).then((r) => setTypes(asList(r))).catch(() => setTypes([]));
  }, []);
  useEffect(load, [load]);
  const { error, run } = useSaver(() => { setEditing(null); load(); });

  return (
    <div>
      <ErrorLine error={error} />
      <Note>
        A designation prints on ID cards and the staff directory; a department is on every employee
        record. Retiring one keeps existing staff attached to it and stops it being offered for new
        work. Retired rows stay visible here, dimmed, so an accidental retire can be undone.
      </Note>

      <h2 style={{ fontSize: 15, margin: '16px 0 8px' }}>Departments</h2>
      {depts.length === 0 ? <Empty what="departments" /> : depts.map((d) => (
        <Row key={d.id} active={d.isActive}>
          {editing === d.id
            ? <NameEdit value={d.name} onSave={(name) => run(() => masterData.updateDepartment(d.id, { name, code: null }))} onCancel={() => setEditing(null)} />
            : (
              <>
                <span style={{ flex: 1, fontWeight: 600 }}>{d.name}</span>
                {!d.isActive && <RetiredPill />}
                <button className="btn" style={{ padding: '2px 10px' }} onClick={() => setEditing(d.id)}>Rename</button>
                <button className="btn" style={{ padding: '2px 10px' }}
                  onClick={() => run(() => masterData.setDepartmentActive(d.id, !d.isActive))}>
                  {d.isActive ? 'Retire' : 'Restore'}
                </button>
              </>
            )}
        </Row>
      ))}

      <h2 style={{ fontSize: 15, margin: '20px 0 8px' }}>Designations</h2>
      {desigs.length === 0 ? <Empty what="designations" /> : desigs.map((d) => (
        <Row key={d.id} active={d.isActive}>
          {editing === d.id
            ? <NameEdit value={d.name} onSave={(name) => run(() => masterData.updateDesignation(d.id, { name, code: null }))} onCancel={() => setEditing(null)} />
            : (
              <>
                <span style={{ flex: 1, fontWeight: 600 }}>{d.name}</span>
                {!d.isActive && <RetiredPill />}
                <button className="btn" style={{ padding: '2px 10px' }} onClick={() => setEditing(d.id)}>Rename</button>
                <button className="btn" style={{ padding: '2px 10px' }}
                  onClick={() => run(() => masterData.setDesignationActive(d.id, !d.isActive))}>
                  {d.isActive ? 'Retire' : 'Restore'}
                </button>
              </>
            )}
        </Row>
      ))}

      <h2 style={{ fontSize: 15, margin: '20px 0 8px' }}>Leave types</h2>
      <Note>
        Changing a quota does <strong>not</strong> re-issue balances already granted. A balance is
        what an employee has actually been given for the year; topping everybody up because somebody
        fixed a typo would hand out days nobody approved. The new quota applies from now on.
      </Note>
      {types.length === 0 ? <Empty what="leave types" /> : types.map((t) => (
        <Row key={t.id} active={t.isActive}>
          {editing === t.id
            ? <LeaveTypeEdit row={t} onSave={(b) => run(() => masterData.updateLeaveType(t.id, b))} onCancel={() => setEditing(null)} />
            : (
              <>
                <span style={{ flex: 1, fontWeight: 600 }}>{t.name}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  {t.annualQuota} days/yr{t.isPaid ? '' : ' · unpaid'}{t.carryForward ? ` · carries up to ${t.maxCarryForward}` : ''}
                </span>
                {!t.isActive && <RetiredPill />}
                <button className="btn" style={{ padding: '2px 10px' }} onClick={() => setEditing(t.id)}>Edit</button>
                <button className="btn" style={{ padding: '2px 10px' }}
                  onClick={() => run(() => masterData.setLeaveTypeActive(t.id, !t.isActive))}>
                  {t.isActive ? 'Retire' : 'Restore'}
                </button>
              </>
            )}
        </Row>
      ))}
    </div>
  );
}

/**
 * ⚠ WHAT THE PUBLIC PAGE SAYS ABOUT ONE INSTITUTION — inline under the row rather than behind a
 * separate screen. This is the field a trust fills in once and then forgets; putting it two clicks
 * away is how a website ends up listing four schools with no description on any of them.
 *
 * ⚠ SAVE IS SHOWN ONLY WHEN SOMETHING CHANGED. A row of always-live Save buttons on a list of
 * twelve institutions invites a clerk to press one and wonder what happened.
 */
function PublicProfileEdit({ row, onSave }: {
  row: InstitutionRow;
  onSave: (b: { blurb: string | null; phone: string | null; admissionsOpen: boolean | null }) => void;
}) {
  const [blurb, setBlurb] = useState(row.publicBlurb ?? '');
  const [phone, setPhone] = useState(row.publicPhone ?? '');
  // ⚠ '' is "follow the trust" (null), not "false". Three states, and the middle one is the default.
  const [adm, setAdm] = useState<string>(
    row.admissionsOpen === true ? 'open' : row.admissionsOpen === false ? 'closed' : '');

  const dirty = blurb !== (row.publicBlurb ?? '')
    || phone !== (row.publicPhone ?? '')
    || adm !== (row.admissionsOpen === true ? 'open' : row.admissionsOpen === false ? 'closed' : '');

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
      padding: '0 12px 12px', marginTop: -2, fontSize: 12.5, color: 'var(--ink-3)' }}>
      <span style={{ minWidth: 58 }}>Website</span>
      <input className="input" value={blurb} onChange={(e) => setBlurb(e.target.value)}
        placeholder="One line a parent reads" maxLength={400} style={{ flex: '2 1 240px' }} />
      <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)}
        placeholder="Phone for this one" maxLength={20} style={{ width: 150 }} />
      <select className="input" value={adm} onChange={(e) => setAdm(e.target.value)} style={{ width: 170 }}>
        <option value="">Follow the trust</option>
        <option value="open">Admissions open</option>
        <option value="closed">Admissions closed</option>
      </select>
      {dirty && (
        <button className="btn btn-accent" style={{ padding: '2px 12px' }}
          onClick={() => onSave({
            blurb: blurb.trim() || null,
            phone: phone.trim() || null,
            admissionsOpen: adm === 'open' ? true : adm === 'closed' ? false : null,
          })}>Save</button>
      )}
    </div>
  );
}

function CampusEdit({ row, onSave, onCancel }: {
  row: CampusRow;
  onSave: (b: { name: string; city: string | null }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [city, setCity] = useState(row.city ?? '');
  return (
    <>
      <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Campus name" style={{ flex: 1, minWidth: 160 }} />
      <input className="input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" style={{ width: 150 }} />
      <button className="btn btn-accent" style={{ padding: '2px 12px' }}
        onClick={() => onSave({ name: name.trim(), city: city.trim() || null })}>Save</button>
      <button className="btn" style={{ padding: '2px 10px' }} onClick={onCancel}>Cancel</button>
    </>
  );
}

function NameEdit({ value, onSave, onCancel }: { value: string; onSave: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value);
  return (
    <>
      <input className="input" value={v} onChange={(e) => setV(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
      <button className="btn btn-accent" style={{ padding: '2px 12px' }} onClick={() => onSave(v.trim())}>Save</button>
      <button className="btn" style={{ padding: '2px 10px' }} onClick={onCancel}>Cancel</button>
    </>
  );
}

function LeaveTypeEdit({ row, onSave, onCancel }: {
  row: LeaveTypeRow;
  onSave: (b: { name: string; code: string | null; annualQuota: number; carryForward: boolean; maxCarryForward: number; isPaid: boolean }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [quota, setQuota] = useState(String(row.annualQuota));
  const [carry, setCarry] = useState(row.carryForward);
  const [maxCarry, setMaxCarry] = useState(String(row.maxCarryForward));
  const [paid, setPaid] = useState(row.isPaid);
  return (
    <>
      <input className="input" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: '1 1 160px' }} />
      <input className="input" type="number" value={quota} onChange={(e) => setQuota(e.target.value)} style={{ width: 80 }} title="Days per year" />
      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5 }}>
        <input type="checkbox" checked={carry} onChange={(e) => setCarry(e.target.checked)} /> Carries forward
      </label>
      {carry && <input className="input" type="number" value={maxCarry} onChange={(e) => setMaxCarry(e.target.value)} style={{ width: 80 }} title="Max carried" />}
      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5 }}>
        <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} /> Paid
      </label>
      <button className="btn btn-accent" style={{ padding: '2px 12px' }}
        onClick={() => onSave({
          name: name.trim(), code: row.code, annualQuota: Number(quota) || 0,
          carryForward: carry, maxCarryForward: carry ? Number(maxCarry) || 0 : 0, isPaid: paid,
        })}>Save</button>
      <button className="btn" style={{ padding: '2px 10px' }} onClick={onCancel}>Cancel</button>
    </>
  );
}

/* ─────────────────────────── vehicles ─────────────────────────── */

function Vehicles() {
  const [rows, setRows] = useState<VehicleRow[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const load = useCallback(() => { masterData.vehicles(true).then((r) => setRows(asList(r))).catch(() => setRows([])); }, []);
  useEffect(load, [load]);
  const { error, run } = useSaver(() => { setEditing(null); load(); });

  if (rows.length === 0) return <Empty what="vehicles" />;

  return (
    <div>
      <ErrorLine error={error} />
      <Note>
        The <strong>driver's mobile is shown to parents</strong> on the transport screen. Until this
        page existed it could be entered once and never corrected — so a parent ringing about a late
        bus reached whoever held that number last year. It is the field on this page that matters.
      </Note>
      {rows.map((v) => (
        <Row key={v.id} active={v.isActive}>
          {editing === v.id
            ? <VehicleEdit row={v} onSave={(b) => run(() => masterData.updateVehicle(v.id, b))} onCancel={() => setEditing(null)} />
            : (
              <>
                <span style={{ flex: 1, fontWeight: 600 }}>{v.name}</span>
                {v.registrationNumber && <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-3)' }}>{v.registrationNumber}</span>}
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  {v.driverName ?? 'no driver named'}{v.driverMobile ? ` · ${v.driverMobile}` : ''}
                </span>
                {!v.isActive && <RetiredPill />}
                <button className="btn" style={{ padding: '2px 10px' }} onClick={() => setEditing(v.id)}>Edit</button>
                <button className="btn" style={{ padding: '2px 10px' }}
                  onClick={() => run(() => masterData.setVehicleActive(v.id, !v.isActive))}>
                  {v.isActive ? 'Off the road' : 'Back on the road'}
                </button>
              </>
            )}
        </Row>
      ))}
    </div>
  );
}

function VehicleEdit({ row, onSave, onCancel }: {
  row: VehicleRow;
  onSave: (b: { name: string; registrationNumber: string | null; capacity: number; driverName: string | null; driverMobile: string | null }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [reg, setReg] = useState(row.registrationNumber ?? '');
  const [cap, setCap] = useState(String(row.capacity));
  const [driver, setDriver] = useState(row.driverName ?? '');
  const [mobile, setMobile] = useState(row.driverMobile ?? '');
  return (
    <>
      <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Bus name" style={{ flex: '1 1 140px' }} />
      <input className="input" value={reg} onChange={(e) => setReg(e.target.value)} placeholder="Reg. no." style={{ width: 130 }} />
      <input className="input" type="number" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="Seats" style={{ width: 80 }} />
      <input className="input" value={driver} onChange={(e) => setDriver(e.target.value)} placeholder="Driver" style={{ width: 140 }} />
      <input className="input" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Driver mobile" style={{ width: 140 }} />
      <button className="btn btn-accent" style={{ padding: '2px 12px' }}
        onClick={() => onSave({
          name: name.trim(), registrationNumber: reg.trim() || null, capacity: Number(cap) || 0,
          driverName: driver.trim() || null, driverMobile: mobile.trim() || null,
        })}>Save</button>
      <button className="btn" style={{ padding: '2px 10px' }} onClick={onCancel}>Cancel</button>
    </>
  );
}

/* ─────────────────────────── the trust itself ─────────────────────────── */

function Trust() {
  const [campuses, setCampuses] = useState<CampusRow[]>([]);
  const [insts, setInsts] = useState<InstitutionRow[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const load = useCallback(() => {
    masterData.campuses().then((r) => setCampuses(asList(r))).catch(() => setCampuses([]));
    masterData.institutions().then((r) => setInsts(asList(r))).catch(() => setInsts([]));
  }, []);
  useEffect(load, [load]);
  const { error, run } = useSaver(() => { setEditing(null); load(); });

  return (
    <div>
      <ErrorLine error={error} />
      <Note>
        The institution name is what prints at the top of every report card, transfer certificate,
        fee receipt and ID card. A trust that mis-typed it at setup, or that legally re-registered
        under a longer name, previously had no way to say so short of creating a second institution —
        which would have stranded every student and every invoice under the old one.{' '}
        <strong>Nothing already printed is reissued</strong>; a 2024 certificate keeps the 2024 name.
      </Note>

      <h2 style={{ fontSize: 15, margin: '16px 0 8px' }}>Campuses</h2>
      {campuses.length === 0 ? <Empty what="campuses" /> : campuses.map((c) => (
        <Row key={c.id} active={c.isActive}>
          {editing === c.id
            ? <CampusEdit row={c} onSave={(b) => run(() => masterData.updateCampus(c.id, b))} onCancel={() => setEditing(null)} />
            : (
              <>
                <span style={{ flex: 1, fontWeight: 600 }}>{c.name}</span>
                {c.city && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{c.city}</span>}
                {!c.isActive && <RetiredPill />}
                <button className="btn" style={{ padding: '2px 10px' }} onClick={() => setEditing(c.id)}>Edit</button>
                <button className="btn" style={{ padding: '2px 10px' }}
                  onClick={() => run(() => masterData.setCampusActive(c.id, !c.isActive))}>
                  {c.isActive ? 'Close' : 'Reopen'}
                </button>
              </>
            )}
        </Row>
      ))}

      <h2 style={{ fontSize: 15, margin: '20px 0 8px' }}>Institutions</h2>
      <Note>
        The <strong>website</strong> line under each one is what a visitor reads in the
        “Our institutions” section of your public page — which only appears when a trust runs more
        than one. Leaving admissions on <em>Follow the trust</em> means it inherits the single
        switch on the public-page screen; set it here when a school and a college open in different
        months.
      </Note>
      {insts.length === 0 ? <Empty what="institutions" /> : insts.map((i) => (
        <div key={i.id}>
        <Row active={i.isActive}>
          {editing === i.id
            ? <NameEdit value={i.name} onSave={(name) => run(() => masterData.updateInstitution(i.id, { name }))} onCancel={() => setEditing(null)} />
            : (
              <>
                <span style={{ flex: 1, fontWeight: 600 }}>{i.name}</span>
                <span className="pill" style={{ fontSize: 11 }}>{TYPE_LABEL[i.type] ?? i.type}</span>
                {i.board !== 'Unspecified' && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{BOARD_LABEL[i.board] ?? i.board}</span>}
                {!i.isActive && <RetiredPill />}
                <button className="btn" style={{ padding: '2px 10px' }} onClick={() => setEditing(i.id)}>Rename</button>
                <button className="btn" style={{ padding: '2px 10px' }}
                  onClick={() => run(() => masterData.setInstitutionActive(i.id, !i.isActive))}>
                  {i.isActive ? 'Close' : 'Reopen'}
                </button>
              </>
            )}
        </Row>
        {i.isActive && (
          <PublicProfileEdit
            row={i}
            onSave={(b) => run(() => masterData.updateInstitutionPublic(i.id, b))}
          />
        )}
        </div>
      ))}
      <Note>
        The <strong>type</strong> of an institution cannot be changed. It decides whether students
        are organised by class, by semester or by batch — flipping it under live data would leave
        every existing class in a shape the new type cannot render. A trust that genuinely adds a
        college adds an institution; it does not convert its school into one.
      </Note>
    </div>
  );
}
